// ** Redux Imports
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { HomePeriod } from '@/nova/mock-data/types';
import { customDays, isHomeInterval, localToday } from '@/nova/pages/home/stats-mapping';

import type { RootState } from '../index';

/**
 * The date range the owner last picked on any studio page with a range picker (Home, Stats and its Website tab,
 * Acquisition, Affiliates reports, a marketing asset's stats). Every one of those pages opens on it, each showing it
 * as closely as its own picker allows (see `pages/home/shared-period.ts`). Null until the owner picks a range, so each
 * page keeps its own default until then. Kept in this browser, for every tab and across reloads, until a logout or an
 * identity swap resets it (`store/index.ts`). A tab already open sees another tab's pick on its next load. The swap is
 * caught only within one load: an owner who signs in by magic link in a fresh tab, with no logout before, starts on the
 * previous owner's pick (a date range, nothing more).
 */
type StudioPeriodState = { period: HomePeriod | null };

/** Where the pick is kept between loads. */
export const STUDIO_PERIOD_KEY = 'ows-studio-period';

const isHomePeriod = (value: string): value is HomePeriod => isHomeInterval(value) || customDays(value) !== null;

/** The pick an earlier load kept, or null when there is none, it is not a period, or storage is unavailable. */
const readKeptPeriod = (): HomePeriod | null => {
  try {
    const kept = localStorage.getItem(STUDIO_PERIOD_KEY);
    return kept !== null && isHomePeriod(kept) ? kept : null;
  } catch {
    return null;
  }
};

/** Whether `period` is this month so far: the reports' This month, which Home can hold only as its days (a run of
 *  dates, or Today on the 1st). Kept, a later load would read it as those fixed days, not as This month. A Home pick
 *  of the same days (Today on the 1st, a run from the 1st to today) looks the same, so it is not kept either. */
const isMonthSoFar = (period: HomePeriod): boolean => {
  const today = localToday(Date.now());
  const monthStart = `${today.slice(0, 8)}01`;
  if (period === 'TODAY') return today === monthStart;
  const run = customDays(period);
  return run !== null && run.to === today && run.from === monthStart;
};

const keepPeriod = (period: HomePeriod | null): void => {
  try {
    if (period === null) localStorage.removeItem(STUDIO_PERIOD_KEY);
    else localStorage.setItem(STUDIO_PERIOD_KEY, period);
  } catch {
    /* storage unavailable: the pick lasts until the next load. */
  }
};

const studioPeriodSlice = createSlice({
  name: 'studioPeriod',
  // Lazy, so storage is read when a store starts, not when this module loads.
  initialState: (): StudioPeriodState => ({ period: readKeptPeriod() }),
  reducers: {
    setStudioPeriod: (state, action: PayloadAction<HomePeriod>) => {
      state.period = action.payload;
      // This month so far is not kept, so the next load opens each page on its own default (This month on the
      // Affiliates reports and the console).
      keepPeriod(isMonthSoFar(action.payload) ? null : action.payload);
    },
    // Starts over on nothing, not on the kept pick the store started from.
    resetStudioPeriod: (): StudioPeriodState => {
      keepPeriod(null);
      return { period: null };
    },
  },
});

export const { setStudioPeriod, resetStudioPeriod } = studioPeriodSlice.actions;

export const selectStudioPeriod = (state: RootState): HomePeriod | null => state.studioPeriod.period;

export default studioPeriodSlice.reducer;
